import useSWR, { useSWRConfig, mutate } from 'swr'
import { queryWithAuth, gql } from '@crystal-eyes/utils/apis/graphqlApi'
import useAuth from '@crystal-eyes/hooks/useAuth'

enum ProfileSection {
  BlindSpots = 'blindspots',
  BuildingTrust = 'building_trust',
  CustomContent = 'custom_content',
  DiscAtHome = 'disc_at_home',
  Traits = 'traits',
  Behavior = 'behavior',
  Energizers = 'energizers',
  Drainers = 'drainers',
  Strengths = 'strengths',
  FirstImpression = 'first_impression',
  ProductDemo = 'product_demo',
  Negotiating = 'negotiating',
  Pricing = 'pricing',
  DrivingAction = 'driving_action',
  FollowingUp = 'following_up',
  Graphics = 'graphics',
  QuickTipsMeetings = 'quick_tips_meetings',
  WritingStyle = 'writing_style',
  WritingAssistant = 'writing_assistant',
  WritingExamples = 'writing_examples',
  TeamRoles = 'team_roles',
  Playbooks = 'playbooks',
  BigFive = 'big_five',
  Enneagram = 'enneagram',
  MyersBriggs = 'myers_briggs',
  StrengthsAssessment = 'strengths_assessment',
  ValuesAssessment = 'values_assessment',
  V2Phrases = 'v2_phrases',
  Disc = 'disc',
}

export type ProfileModuleName = `${ProfileSection}`

export type TeamLayoutSettings = {
  [key in ProfileModuleName]: boolean
}

const ORG_LAYOUT_SETTINGS_QUERY = gql`
  query OrgLayoutSettings {
    org {
      profileLayoutSettings {
        blindspots
        building_trust
        custom_content
        disc_at_home
        traits
        behavior
        energizers
        drainers
        strengths
        first_impression
        product_demo
        negotiating
        pricing
        driving_action
        following_up
        quick_tips_meetings
        writing_style
        writing_assistant
        writing_examples
        team_roles
        playbooks
        big_five
        enneagram
        myers_briggs
        strengths_assessment
        values_assessment
        graphics
      }
    }
  }
`

export type State = {
  data?: TeamLayoutSettings
  error?: any
  loading?: boolean
  refetch: () => void
}

export default function useOrgLayoutSettings(): State {
  const { data: auth } = useAuth()
  const config = useSWRConfig()
  const key = `global.user.orgLayoutSettings`
  const fetcher = ([_key, _authedUser]: [string, string]) =>
    auth?.authToken ? queryWithAuth(auth, ORG_LAYOUT_SETTINGS_QUERY) : null

  const refetch = () => {
    for (const key of Array.from(config.cache.keys())) {
      if (key.includes(key)) {
        mutate(key)
      }
    }
  }

  const {
    data: queryData,
    isLoading: loading,
    error: error,
  } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  })

  return {
    data: queryData?.org?.profileLayoutSettings,
    loading,
    refetch,
    error,
  }
}
